import { useMemo, useState } from 'react';
import type {
  CustomFieldDefinition,
  CustomFieldEntityType,
  CustomFieldValue,
  CustomFieldValueData,
} from '@neuroedge/shared';
import { Button, Card, PageState, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { useCustomFieldDefinitions } from '../../api/custom-fields';

interface Props {
  entityType: CustomFieldEntityType;
  values: CustomFieldValue[] | undefined;
  /** Full set of `{ fieldKey: value | null }` for the managed definitions. */
  onSave: (values: Record<string, CustomFieldValueData>) => Promise<unknown>;
}

type FormState = Record<string, CustomFieldValueData>;

function seed(defs: CustomFieldDefinition[], values: CustomFieldValue[]): FormState {
  const byKey = new Map(values.map((v) => [v.fieldKey, v.value]));
  const state: FormState = {};
  for (const d of defs) {
    const v = byKey.get(d.fieldKey);
    state[d.fieldKey] =
      v ?? (d.dataType === 'MULTI_SELECT' ? [] : d.dataType === 'BOOLEAN' ? false : '');
  }
  return state;
}

function displayValue(def: CustomFieldDefinition, value: CustomFieldValueData): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ') || '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (def.dataType === 'DATETIME') return new Date(String(value)).toLocaleString();
  if (def.dataType === 'DATE') return new Date(String(value)).toLocaleDateString();
  return String(value);
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Per-value validation mirroring the backend's `coerce()` switch
 * (custom-fields.service.ts): each value is checked against its OWN
 * definition's `dataType`. TEXT/PHONE/SELECT/MULTI_SELECT/BOOLEAN inputs are
 * already constrained by their native control (dropdown, checkbox), so the
 * only client-checkable failure modes are a malformed NUMBER/DATE/DATETIME -
 * left as-is these would otherwise be silently cleared by `toApi` rather than
 * surfaced to the user. Keyed by `fieldKey` (matches `<Field>`'s own ids).
 */
function validateValues(defs: CustomFieldDefinition[], form: FormState): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const d of defs) {
    const raw = form[d.fieldKey];
    if (raw === '' || raw === null || raw === undefined) continue; // empty clears the field
    if (d.dataType === 'NUMBER') {
      if (typeof raw !== 'string' && typeof raw !== 'number') continue;
      if (!Number.isFinite(Number(raw))) {
        errors[d.fieldKey] = `${d.label} must be a number.`;
      }
    } else if (d.dataType === 'DATE') {
      if (typeof raw === 'string' && (!DATE_RE.test(raw) || Number.isNaN(Date.parse(raw)))) {
        errors[d.fieldKey] = `${d.label} must be a valid date.`;
      }
    } else if (d.dataType === 'DATETIME') {
      if (typeof raw === 'string' && Number.isNaN(Date.parse(raw))) {
        errors[d.fieldKey] = `${d.label} must be a valid date and time.`;
      }
    }
  }
  return errors;
}

/** Convert a form value to what the API expects, or `null` to clear it. */
function toApi(def: CustomFieldDefinition, raw: CustomFieldValueData): CustomFieldValueData {
  if (def.dataType === 'BOOLEAN') return Boolean(raw);
  if (def.dataType === 'MULTI_SELECT') return Array.isArray(raw) && raw.length ? raw : null;
  if (raw === '' || raw === null || raw === undefined) return null;
  if (def.dataType === 'NUMBER') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (def.dataType === 'DATETIME') return new Date(String(raw)).toISOString();
  return String(raw);
}

function Field({
  def,
  value,
  onChange,
  error,
}: {
  def: CustomFieldDefinition;
  value: CustomFieldValueData;
  onChange: (v: CustomFieldValueData) => void;
  error?: string;
}) {
  const id = `cf-${def.fieldKey}`;
  const label = (
    <label className="ne-field__label" htmlFor={id}>
      {def.label}
      {def.required ? ' *' : ''}
    </label>
  );
  const errorNode = error ? (
    <span id={`${id}-error`} className="ne-field__error" role="alert">
      {error}
    </span>
  ) : null;

  if (def.dataType === 'BOOLEAN') {
    return (
      <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <input
          id={id}
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        {def.label}
        {def.required ? ' *' : ''}
      </label>
    );
  }

  if (def.dataType === 'SELECT') {
    return (
      <div className="ne-field">
        {label}
        <select
          id={id}
          className="ne-input"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">—</option>
          {def.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (def.dataType === 'MULTI_SELECT') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="ne-field">
        {label}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {def.options.map((o) => (
            <label key={o} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={arr.includes(o)}
                onChange={(e) =>
                  onChange(e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))
                }
              />
              {o}
            </label>
          ))}
        </div>
      </div>
    );
  }

  const inputType =
    def.dataType === 'NUMBER'
      ? 'number'
      : def.dataType === 'DATE'
        ? 'date'
        : def.dataType === 'DATETIME'
          ? 'datetime-local'
          : 'text';

  return (
    <div className="ne-field">
      {label}
      <input
        id={id}
        className={['ne-input', error ? 'ne-input--error' : ''].filter(Boolean).join(' ')}
        type={inputType}
        value={
          value === null || Array.isArray(value) || typeof value === 'boolean' ? '' : String(value)
        }
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {errorNode}
    </div>
  );
}

export function CustomFieldValues({ entityType, values, onSave }: Props) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(entityType === 'CONTACT' ? 'contacts.update' : 'leads.update');
  const defs = useCustomFieldDefinitions(entityType);
  const toast = useToast();

  const current = useMemo(() => values ?? [], [values]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function begin() {
    if (defs.data) setForm(seed(defs.data, current));
    setError(null);
    setFieldErrors({});
    setEditing(true);
  }

  async function save() {
    if (!defs.data) return;

    const errors = validateValues(defs.data, form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, CustomFieldValueData> = {};
      for (const d of defs.data) payload[d.fieldKey] = toApi(d, form[d.fieldKey] ?? null);
      await onSave(payload);
      setEditing(false);
      toast.success('Custom fields updated.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save custom fields.';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  if (defs.isPending) {
    return (
      <Card title="Custom fields">
        <PageState kind="loading" message="Loading custom fields…" />
      </Card>
    );
  }
  if (defs.isError) {
    return (
      <Card title="Custom fields">
        <PageState kind="error" message={defs.error.message} onRetry={() => void defs.refetch()} />
      </Card>
    );
  }
  if (defs.data.length === 0) return null;

  const byKey = new Map(current.map((v) => [v.fieldKey, v.value]));

  return (
    <Card title="Custom fields">
      {!editing ? (
        <>
          <dl
            style={{
              margin: 0,
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: 'var(--space-2) var(--space-4)',
            }}
          >
            {defs.data.map((d) => (
              <div key={d.id} style={{ display: 'contents' }}>
                <dt style={{ color: 'var(--color-text-muted)' }}>{d.label}</dt>
                <dd style={{ margin: 0 }}>{displayValue(d, byKey.get(d.fieldKey) ?? null)}</dd>
              </div>
            ))}
          </dl>
          {canEdit ? (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Button variant="secondary" onClick={begin}>
                Edit custom fields
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {defs.data.map((d) => (
            <Field
              key={d.id}
              def={d}
              value={form[d.fieldKey] ?? null}
              onChange={(v) => {
                setForm((f) => ({ ...f, [d.fieldKey]: v }));
                setFieldErrors((f) => {
                  if (!f[d.fieldKey]) return f;
                  const { [d.fieldKey]: _removed, ...rest } = f;
                  return rest;
                });
              }}
              error={fieldErrors[d.fieldKey]}
            />
          ))}
          {error ? (
            <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
              {error}
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button disabled={saving} onClick={() => void save()}>
              Save
            </Button>
            <Button variant="secondary" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
